/* Procedures — handover §14 step 11, and the second of the two mechanisms the
   README says must survive the build.

   The first is the article review flag. This is the other one: the quarterly
   review checklist, seeded as template `t4`. A register decays unless something
   makes a person walk it, and a checklist run is that something — it turns "we
   should check the data" into a list with ticks against it and a name on it.

   The same machinery does starters, leavers and device builds, which is the
   point: those are the moments the registers go wrong, so the checklist that
   gets followed at a leaver's desk is the thing that stops a licence being paid
   for another eleven months.

   A run is deliberately not deletable. A half-finished leaver checklist from
   March is evidence about what happened in March, and tidying it away is how
   you lose the answer to "was the account ever disabled?". */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOnline } from '../../hooks/useOnline';
import { fmtDate } from '../../lib/format';
import type { IChecklistRun, IChecklistTemplate } from '../../models';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  Drawer,
  EmptyState,
  LoadError,
  Meter,
  Panel,
  Segmented,
  TableSkeleton,
} from '../ui';
import s from './screens.module.scss';

export function ProceduresScreen() {
  const svc = useServices();
  const online = useOnline();
  const qc = useQueryClient();

  const [lens, setLens] = useState('open');
  const [openRun, setOpenRun] = useState<IChecklistRun | null>(null);
  const [starting, setStarting] = useState<IChecklistTemplate | null>(null);
  const [subject, setSubject] = useState('');

  const templates = useQuery({
    queryKey: qk.templates,
    queryFn: () => svc.procedures.templates(),
  });
  const runs = useQuery({ queryKey: qk.runs, queryFn: () => svc.procedures.runs() });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.runs });
    void qc.invalidateQueries({ queryKey: qk.audit });
  };

  const start = useMutation({

    meta: { ok: 'Checklist started' },
    mutationFn: ({ templateId, name }: { templateId: string; name: string }) =>
      svc.procedures.startRun(templateId, name),
    onSuccess: (run) => {
      refresh();
      setStarting(null);
      setSubject('');
      /* Straight into the run. Starting a checklist and then having to find it
         in a list is how the first three steps get skipped. */
      setOpenRun(run);
    },
  });

  const toggle = useMutation({
    mutationFn: ({ runId, index, done }: { runId: string; index: number; done: boolean }) =>
      svc.procedures.toggleStep(runId, index, done),
    onSuccess: refresh,
  });

  const templateOf = (id: string) => (templates.data ?? []).find((t) => t.id === id);

  const steps = (run: IChecklistRun) => templateOf(run.tmpl)?.steps ?? [];
  const isComplete = (run: IChecklistRun) => {
    const total = steps(run).length;
    return total > 0 && run.done.length >= total;
  };

  const visibleRuns = useMemo(() => {
    const all = [...(runs.data ?? [])].sort((a, b) => b.started.localeCompare(a.started));
    if (lens === 'open') return all.filter((r) => !isComplete(r));
    if (lens === 'done') return all.filter(isComplete);
    return all;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs.data, templates.data, lens]);

  /* The run drawer reads from the query rather than from the state it was
     opened with, so ticking a step redraws the progress instead of showing the
     figures from the moment it opened. */
  const live = openRun ? (runs.data ?? []).find((r) => r.id === openRun.id) ?? openRun : null;

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Procedures</h1>
          <p>
            Starters, leavers, device builds and the quarterly review. Each run records who did
            what and when.
          </p>
        </div>
      </div>

      {(templates.isError || runs.isError) && (
        <LoadError error={templates.error ?? runs.error} what="procedures" />
      )}

      <div className={s.stack}>
        <Panel title="Start a checklist" sub="Pick the procedure, name what it is for">
          {templates.isLoading ? (
            <TableSkeleton rows={3} />
          ) : (
            <div className={s.tiles}>
              {(templates.data ?? []).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={s.tile}
                  style={{ cursor: 'pointer', textAlign: 'left' }}
                  disabled={!online}
                  onClick={() => {
                    setStarting(t);
                    setSubject('');
                  }}
                >
                  <span className={s.tileIcon}>
                    <i className={`ti ${t.icon || 'ti-checklist'}`} aria-hidden />
                  </span>
                  <div className={s.min0}>
                    <b className={s.tileValue}>{t.name}</b>
                    <span className={s.tileLabel}>{t.steps.length} steps</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Panel>

        <Panel
          title="Runs"
          sub="Open first"
          flush
          actions={
            <Segmented
              label="Show"
              value={lens}
              onChange={setLens}
              options={[
                { value: 'open', label: 'In progress' },
                { value: 'done', label: 'Finished' },
                { value: 'all', label: 'All' },
              ]}
            />
          }
        >
          {runs.isLoading ? (
            <TableSkeleton />
          ) : visibleRuns.length === 0 ? (
            <EmptyState icon="ti-checklist" title={lens === 'open' ? 'Nothing in progress' : 'No runs yet'}>
              {lens === 'open'
                ? 'Everything started has been finished — which is the state you want to find this screen in.'
                : 'Start one above.'}
            </EmptyState>
          ) : (
            <div className={s.rows}>
              {visibleRuns.map((run) => {
                const total = steps(run).length;
                const done = run.done.length;
                const complete = isComplete(run);
                return (
                  <div key={run.id} className={s.row}>
                    <span className={s.rowIcon}>
                      <i
                        className={`ti ${templateOf(run.tmpl)?.icon ?? 'ti-checklist'}`}
                        aria-hidden
                      />
                    </span>
                    <button
                      type="button"
                      className={`${s.rowMain} ${s.rowButton}`}
                      onClick={() => setOpenRun(run)}
                    >
                      <div className={s.rowTitle}>{run.subject}</div>
                      <div className={s.rowSub}>
                        {templateOf(run.tmpl)?.name ?? 'Unknown procedure'} · started{' '}
                        {fmtDate(run.started)}
                      </div>
                    </button>
                    <div className={s.expBar}>
                      <Meter value={done} max={total || 1} tone={complete ? 'ok' : 'warn'} />
                    </div>
                    <Badge tone={complete ? 'ok' : 'warn'}>
                      {done}/{total}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>

      {/* Naming the run. A checklist called "Leaver" with no name on it is
          useless a fortnight later. */}
      <Drawer
        open={starting !== null}
        title={starting?.name ?? ''}
        sub={`${starting?.steps.length ?? 0} steps`}
        avatar={<i className={`ti ${starting?.icon ?? 'ti-checklist'}`} aria-hidden />}
        onClose={() => setStarting(null)}
      >
        {starting && (
          <>
            <div className={s.field}>
              <label htmlFor="run-subject">What is this for?</label>
              <input
                id="run-subject"
                value={subject}
                placeholder="A person, a device, or the quarter"
                onChange={(e) => setSubject(e.target.value)}
              />
              <div className={s.hint}>
                Names the run in the list — "J. Whitmore", "HIRD-LT-014", "Q3 2026".
              </div>
            </div>

            <div>
              <div className={s.sectionLabel}>Steps</div>
              <ol className={s.steps}>
                {starting.steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>

            <div className={s.actions}>
              <Button
                primary
                icon="ti-player-play"
                disabled={!online || start.isPending || subject.trim() === ''}
                onClick={() => start.mutate({ templateId: starting.id, name: subject.trim() })}
              >
                {start.isPending ? 'Starting…' : 'Start'}
              </Button>
            </div>
          </>
        )}
      </Drawer>

      {/* Working through a run. */}
      <Drawer
        open={live !== null}
        title={live?.subject ?? ''}
        sub={live ? templateOf(live.tmpl)?.name : undefined}
        avatar={<i className={`ti ${live ? templateOf(live.tmpl)?.icon ?? 'ti-checklist' : ''}`} aria-hidden />}
        meta={
          live && (
            <>
              <Badge tone={isComplete(live) ? 'ok' : 'warn'}>
                {live.done.length}/{steps(live).length} done
              </Badge>
              <Badge tone="muted">Started {fmtDate(live.started)}</Badge>
            </>
          )
        }
        onClose={() => setOpenRun(null)}
      >
        {live && (
          <>
 <div className={`${s.stack} ${s.rowGap}`} >
              {steps(live).map((step, i) => {
                const done = live.done.includes(i);
                return (
                  <label key={i} className={`${s.checkStep} ${done ? s.checkStepDone : ''}`}>
                    <input
                      type="checkbox"
                      checked={done}
                      disabled={!online || toggle.isPending}
                      onChange={(e) =>
                        toggle.mutate({ runId: live.id, index: i, done: e.target.checked })
                      }
                    />
                    <span>{step}</span>
                  </label>
                );
              })}
            </div>

            {isComplete(live) ? (
              <div className={s.ownedNote}>
                <i className="ti ti-circle-check" aria-hidden />
                <span>Every step done. The run stays on the list as the record that it was.</span>
              </div>
            ) : (
              <div className={s.ownedNote}>
                <i className="ti ti-history" aria-hidden />
                <span>
                  Ticks save as you go, so a run can be picked up later. Runs are never deleted — a
                  half-finished leaver checklist is evidence about what happened, and tidying it
                  away loses the answer to "was the account ever disabled?".
                </span>
              </div>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
